import React, { useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import PermanentRedirect from '../../components/SeoHelmet/PermanentRedirect';
import PendingHead from '../../components/SeoHelmet/PendingHead';
import ErrorPage from '../../pages/shared/error/ErrorPage/ErrorPage';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { authTokenState, roleAccessState } from '../../../atoms/auth/auth.atom';
import { routes } from '../../../config/routes/routes';
import LazyLoad from './LazyLoading/LazyLoading';
import NotFoundPage from '../../pages/shared/error/NotFoundPage/NotFoundPage';
import { useMutation } from '@tanstack/react-query';
import axios, { SuccessResponse } from '../../../libraries/axios/axios';
import { MappedRoleAccess } from '../../../domain/Role';
import { DefaultError } from '@tanstack/query-core/build/modern/index';
import { apiRoutes } from '../../../config/api/apiRoutes';
import { useStaticPagesQuery } from '../../../api/static-pages/queries';
import { currentCartItemsState } from '../../../atoms/cart/cart.atom';
import { useCreateOrUpdateUserBasketMutation } from '../../../api/basket/mutations';
import {
  getLocalStorageValue,
  LocalStorageKey,
  setLocalStorageValue,
} from '../../../utils/localStorage/localStorage';
import { v4 as uuidv4 } from 'uuid';
import { useInitCurrentVatRateMutation } from '../../../api/country/mutations';
import { VatRate } from '../../../domain/VatRate';
import { useGetVatRatesQuery } from '../../../api/vat-rates/queries';
import { currentVatRateState } from '../../../atoms/vat-rate/vat-rate.atom';
import PageTracker from '../../components/PageTracker/PageTracker';
import PaymentStatusRedirect from '../../components/PaymentStatusRedirect/PaymentStatusRedirect';

/* eslint-disable new-cap */
const Homepage = LazyLoad(() => import('../../pages/Homepage/Homepage'));
const CatalogPage = LazyLoad(
  () => import('../../pages/CatalogPage/CatalogPage'),
);
const ProductsPage = LazyLoad(
  () => import('../../pages/ProductsPage/ProductsPage'),
);
const ProductPage = LazyLoad(
  () => import('../../pages/ProductPage/ProductPage'),
);
const CheckoutPage = LazyLoad(
  () => import('../../pages/CheckoutPage/CheckoutPage'),
);
const CheckoutOrderPage = LazyLoad(
  () => import('../../pages/CheckoutOrderPage/CheckoutOrderPage'),
);
const CheckoutRedirectPage = LazyLoad(
  () => import('../../pages/CheckoutRedirectPage/./CheckoutRedirectPage'),
);
const OrderSuccessPage = LazyLoad(
  () => import('../../pages/OrderSuccessPage/OrderSuccessPage'),
);
const ProfileAddressPage = LazyLoad(
  () => import('../../pages/profile/AddressPage/AddressPage'),
);
const ProfileBankDetailsPage = LazyLoad(
  () => import('../../pages/profile/BankDetailsPage/BankDetailsPage'),
);
const ProfileFavoritesPage = LazyLoad(
  () => import('../../pages/profile/FavoritesPage/FavoritesPage'),
);
const ProfileOrdersPage = LazyLoad(
  () => import('../../pages/profile/OrdersPage/OrdersPage'),
);
const ProfileSettingsPage = LazyLoad(
  () => import('../../pages/profile/SettingsPage/SettingsPage'),
);
const ProfileVehiclesPage = LazyLoad(
  () => import('../../pages/profile/VehiclesPage/VehiclesPage'),
);
const ArticleCategoriesPage = LazyLoad(
  () =>
    import('../../pages/articles/ArticleCategoriesPage/ArticleCategoriesPage'),
);
const ArticleCategoryPage = LazyLoad(
  () => import('../../pages/articles/ArticleCategoryPage/ArticleCategoryPage'),
);
const ArticlePage = LazyLoad(
  () => import('../../pages/articles/ArticlePage/ArticlePage'),
);
const ReturnsPage = LazyLoad(
  () => import('../../pages/ReturnsPage/ReturnsPage'),
);
const OrderStatusPage = LazyLoad(
  () => import('../../pages/OrderStatusPage/OrderStatusPage'),
);
const StaticPage = LazyLoad(() => import('../../pages/StaticPage/StaticPage'));

const PublicLayout = React.lazy(
  () => import('../../layout/PublicLayout/PublicLayout'),
);
const BasketLayout = React.lazy(
  () => import('../../layout/BasketLayout/BasketLayout'),
);
const ProfileLayout = React.lazy(
  () => import('../../layout/ProfileLayout/ProfileLayout'),
);
/* eslint-enable new-cap */

export const RouteProvider = () => {
  const authStateValue = useRecoilValue(authTokenState);
  const setRoleAccessStateValue = useSetRecoilState(roleAccessState);
  const [cartItems] = useRecoilState(currentCartItemsState);
  const currentVatRate = useRecoilValue(currentVatRateState);

  const { mutate: getMappedRoleAccess } = useMutation<
    SuccessResponse<MappedRoleAccess[]>,
    DefaultError
  >({
    mutationFn: () => axios.get(apiRoutes.roles.mappedAccess),
    onSuccess: (response) => {
      setRoleAccessStateValue(response.data);
    },
  });

  const { data: vatRates } = useGetVatRatesQuery();
  const { mutate: initCurrentVatRate } = useInitCurrentVatRateMutation();

  const getToken = () => {
    const existingToken = getLocalStorageValue<string>(
      LocalStorageKey.basketId,
    );

    if (existingToken) {
      return existingToken;
    }

    const token = uuidv4();
    setLocalStorageValue(LocalStorageKey.basketId, token);

    return token;
  };

  const { mutate: createOrUpdateBasket } =
    useCreateOrUpdateUserBasketMutation();

  useEffect(() => {
    if (!authStateValue) {
      return;
    }

    if (cartItems.length > 0) {
      createOrUpdateBasket({
        token: getToken(),
        basketItems: cartItems.map((cartItem) => ({
          product: cartItem.productSku,
          manufacturer: cartItem.manufacturer,
          quantity: cartItem.quantity,
        })),
      });
    }
  }, [authStateValue, cartItems]);

  const { data: staticPageArr, isPending: isStaticPagesPending } =
    useStaticPagesQuery();

  useEffect(() => {
    if (authStateValue) {
      getMappedRoleAccess();
    }
  }, [authStateValue]);

  useEffect(() => {
    if (!currentVatRate && vatRates.length > 0) {
      initCurrentVatRate(vatRates);
    }
  }, [vatRates]);

  useEffect(() => {
    if (currentVatRate) {
      setLocalStorageValue<VatRate>(LocalStorageKey.vatRate, currentVatRate);
    }
  }, [currentVatRate]);

  const publicPages = useMemo(() => {
    return (
      <>
        <Route path={routes.catalog} element={<CatalogPage />} />
        <Route path={routes.brandCatalog} element={<CatalogPage />} />
        <Route path={routes.vehicleCatalog} element={<CatalogPage />} />
        <Route path={routes.deepCategoryCatalog} element={<CatalogPage />} />
        <Route path={routes.product} element={<ProductPage />} />
        <Route path={routes.categoryProducts} element={<ProductsPage />} />
        <Route path={routes.productOemCode} element={<ProductsPage />} />
        <Route
          path={routes.parentCategoryProducts}
          element={<ProductsPage />}
        />
        <Route path={routes.deepCategoryProducts} element={<ProductsPage />} />
        <Route path={routes.products} element={<ProductsPage />} />
        {/* A real 301 for crawlers (PermanentRedirect emits the prerender
            meta pair); <Navigate> alone left /articles as a 200 duplicate of
            the categories page in the snapshot cache. */}
        <Route
          path={routes.articles.index}
          element={<PermanentRedirect to={routes.articles.categories} />}
        />
        <Route
          path={routes.articles.categories}
          element={<ArticleCategoriesPage />}
        />
        <Route
          path={routes.articles.category}
          element={<ArticleCategoryPage />}
        />
        <Route path={routes.articles.article} element={<ArticlePage />} />
        <Route path={routes.returns} element={<ReturnsPage />} />
        <Route path={routes.orderStatus} element={<OrderStatusPage />} />
        {/* /cart is the convention shoppers type in or keep bookmarked, but
            the basket lives at /checkout - without this they land on a 404
            while holding a full basket. */}
        <Route
          path={routes.cart}
          element={<PermanentRedirect to={routes.checkout} />}
        />
        <Route path={routes.homepage} element={<Homepage />} />
      </>
    );
  }, []);

  const basketPages = useMemo(() => {
    return (
      <>
        <Route path={routes.checkout} element={<CheckoutPage />} />
        <Route path={routes.checkoutOrder} element={<CheckoutOrderPage />} />
        <Route
          path={routes.checkoutRedirect}
          element={<CheckoutRedirectPage />}
        />
        <Route path={routes.orderSuccess} element={<OrderSuccessPage />} />
      </>
    );
  }, []);

  const profilePages = useMemo(() => {
    if (authStateValue) {
      return (
        <>
          <Route
            path={routes.profile.address}
            element={<ProfileAddressPage />}
          />
          <Route
            path={routes.profile.bankDetails}
            element={<ProfileBankDetailsPage />}
          />
          <Route
            path={routes.profile.favorites}
            element={<ProfileFavoritesPage />}
          />
          <Route path={routes.profile.orders} element={<ProfileOrdersPage />} />
          <Route
            path={routes.profile.settings}
            element={<ProfileSettingsPage />}
          />
          <Route
            path={routes.profile.vehicles}
            element={<ProfileVehiclesPage />}
          />
        </>
      );
    }
  }, [authStateValue]);

  const staticPages = useMemo(() => {
    return (
      <>
        {staticPageArr?.map((staticPage) => (
          <Route
            key={staticPage.id}
            path={routes.staticPage.replace(
              ':slug',
              staticPage.seo?.slug ?? staticPage.name,
            )}
            element={<StaticPage name={staticPage.name} />}
          />
        ))}
      </>
    );
  }, [staticPageArr]);

  return (
    <BrowserRouter>
      <PageTracker />
      <PaymentStatusRedirect />
      {/* Without a boundary above the lazy layouts, a failed chunk load or a
          render error unmounts the entire tree, leaving an empty #root */}
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <Routes>
          <Route
            element={
              <React.Suspense fallback={null}>
                <PublicLayout />
              </React.Suspense>
            }
          >
            {publicPages}
            {staticPages}
            {/* Static-page routes are not known until their list arrives, so
                an unmatched path cannot be called a 404 yet. The bare null
                used to be snapshotted as an empty 200 (title-less, for a
                week); the 503 head makes the renderer refuse it instead. */}
            {isStaticPagesPending ? (
              <Route path="*" element={<PendingHead />} />
            ) : (
              <Route path="*" element={<NotFoundPage />} />
            )}
          </Route>
          <Route
            element={
              <React.Suspense fallback={null}>
                <BasketLayout />
              </React.Suspense>
            }
          >
            {basketPages}
          </Route>
          <Route
            element={
              <React.Suspense fallback={null}>
                <ProfileLayout />
              </React.Suspense>
            }
          >
            {profilePages}
          </Route>
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default RouteProvider;
